import { motion } from "framer-motion";
import {
  Lock,
  ConciergeBell,
  HandCoins,
  Car,
  Undo2,
  ShieldQuestion,
} from "lucide-react";

const STEPS = [
  {
    icon: ConciergeBell,
    title: "Ask at reception",
    text: "Mention you'd like to borrow a steering wheel lock when you check in. It's free with your stay.",
  },
  {
    icon: HandCoins,
    title: "Leave a small holding deposit",
    text: "The desk takes a small refundable deposit — a card pre-authorisation or cash. You get it back in full when the lock is returned.",
  },
  {
    icon: Car,
    title: "Fit it in seconds",
    text: "Loop the lock over your steering wheel before you leave the car park. Its bright, high-visibility design is the point — it says \"move along\" to opportunistic thieves.",
  },
  {
    icon: Undo2,
    title: "Return it at checkout",
    text: "Hand the lock back at reception and your holding deposit is released on the spot.",
  },
];

export default function Guest() {
  return (
    <div data-testid="guest-page" className="min-h-screen bg-background">
      <header className="border-b border-border bg-white">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary">
              <Lock className="h-4 w-4 text-white" />
            </span>
            <span className="font-serif text-lg font-semibold tracking-tight text-primary">
              SecureStay Locks
            </span>
          </div>
          <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            Guest guide
          </p>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-14 md:py-20">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <p className="text-xs font-semibold uppercase tracking-widest text-accent">
            A free amenity from your hotel
          </p>
          <h1 className="mt-3 font-serif text-4xl md:text-5xl tracking-tight text-primary leading-[1.1]">
            Borrow a steering wheel lock for your stay.
          </h1>
          <p className="mt-5 text-base md:text-lg leading-relaxed text-muted-foreground">
            Your hotel lends guests a high-visibility steering wheel lock at no
            charge — a simple, visible deterrent while your car is parked on
            site. Here's how it works.
          </p>
        </motion.div>

        <div className="mt-12 space-y-4">
          {STEPS.map((step, i) => (
            <motion.div
              key={step.title}
              data-testid={`guest-step-${i + 1}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="flex gap-5 rounded-lg border border-border bg-white p-6"
            >
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-accent">
                <step.icon className="h-5 w-5" />
              </span>
              <div>
                <h2 className="font-serif text-lg tracking-tight text-primary">
                  {i + 1}. {step.title}
                </h2>
                <p className="mt-1.5 text-sm md:text-base leading-relaxed text-muted-foreground">
                  {step.text}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          data-testid="guest-honesty-note"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mt-10 rounded-lg border border-border bg-blue-50/60 p-6 md:p-8"
        >
          <div className="flex gap-4">
            <ShieldQuestion className="h-6 w-6 shrink-0 text-accent" />
            <div>
              <h2 className="font-serif text-lg tracking-tight text-primary">
                An honest note
              </h2>
              <p className="mt-2 text-sm md:text-base leading-relaxed text-muted-foreground">
                No single device stops a determined thief. A visible lock deters
                opportunistic drive-off and relay theft — the most common kinds
                — but it won't stop a tow truck. Think of it as an added
                deterrent and a little extra peace of mind, provided as-is by
                your hotel.
              </p>
            </div>
          </div>
        </motion.div>

        <p className="mt-10 text-center text-sm text-muted-foreground">
          Questions? Just ask at reception — they'll set you up in under a minute.
        </p>
      </main>
    </div>
  );
}
